import { Form } from 'antd';
import React, { useState } from 'react';

import {
  Banner,
  Card,
  ErrorHandler,
  Flex,
  Gap,
  Gutter,
  LoadingState,
  SubmitButton,
  Text,
  TimeWithTimeZone,
} from '@common/components';
import { AcceptSupplierSignupInvitationInput } from '@common/graphql';
import { useMutation, useQuery, useNavigate, useParams } from '@common/hooks';
import { AuthTemplateHelp, AuthTemplateLogo } from '@identity/components';
import {
  ACCEPT_SUPPLIER_SIGNUP_INVITATION_MUTATION,
  SUPPLIER_SIGNUP_INVITATION_QUERY,
} from '@identity/graphql';
import { TimeZonesQuery, TimeZonesQueryResponse } from '@institutions/graphql';
import { createMessage } from 'works/legacy/lib/message-helper';
import { SupplierSignupPageOrganizationDetailsForm } from './_supplier-sign-up-page-organization-details-form';
import { SupplierSignupPageUserProfileForm } from './_supplier-sign-up-page-user-profile-form';

export function SupplierSignUpPage() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [form] = Form.useForm();
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [acceptSupplierSignupInvitation] = useMutation(
    ACCEPT_SUPPLIER_SIGNUP_INVITATION_MUTATION
  );

  const {
    data: invitationData,
    loading: invitationLoading,
    error: invitationError,
  } = useQuery(SUPPLIER_SIGNUP_INVITATION_QUERY, {
    variables: { token: token || '' },
    skip: !token,
  });

  const {
    data: timeZonesQueryData,
    error: timeZonesQueryError,
    loading: timeZonesQueryLoading,
  } = useQuery<TimeZonesQueryResponse>(TimeZonesQuery);

  const error = invitationError || timeZonesQueryError;
  const loading = invitationLoading || timeZonesQueryLoading;

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorHandler error={error} log="SupplierSignupForm" />;
  }

  type SupplierSignupFormValuesType =
    Partial<AcceptSupplierSignupInvitationInput> & {
      passwordConfirmation?: string;
    };

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const invitation = invitationData?.supplierSignupInvitation;

  const pageGapSize = 'xl';

  const onSubmit = async (values: SupplierSignupFormValuesType) => {
    if (!token) {
      return;
    }

    setFormSubmitting(true);

    const input = {
      ...values,
      passwordConfirmation: undefined,
    } as AcceptSupplierSignupInvitationInput;

    try {
      const { data, errors } = await acceptSupplierSignupInvitation({
        variables: {
          token,
          input,
        },
      });

      if (errors) {
        createMessage('error', errors.map(({ message }) => message).join(' '));
        setFormSubmitting(false);
        return;
      }

      if (data) {
        createMessage(
          'success',
          <>
            Profile and Organization created <strong>successfully</strong>.
            Please, log in to continue.
          </>
        );

        navigate('/works/sign_in');
      }
    } catch (error) {
      createMessage(
        'error',
        'Failed to submit the form. Please try again or contact support.'
      );
    } finally {
      setFormSubmitting(false);
    }
  };

  const initialValues: SupplierSignupFormValuesType = {
    email: invitation?.email,
    supplierName: invitation?.supplier?.name,
  };

  return (
    <div className="supplier-sign-up-page">
      <Gutter size="xxxl" internal>
        <Gap size={pageGapSize} vertical>
          <AuthTemplateLogo />

          <Gap size="md" vertical>
            <Text>
              To activate your organization and profile, fill out the form
              below.
            </Text>

            {invitation?.expiresAt && (
              <Banner
                content={
                  <>
                    It takes only 1-2 minutes of your time to fill. This
                    invitation expires on{' '}
                    <TimeWithTimeZone
                      date={invitation.expiresAt}
                      variant="long_format"
                      timeZone={timeZone}
                    />
                  </>
                }
                type="info"
              />
            )}
          </Gap>

          <Form
            layout="vertical"
            form={form}
            onFinish={onSubmit}
            initialValues={initialValues}
            style={{ width: '100%' }}
          >
            <Gap size={pageGapSize} vertical>
              <Card>
                <Gutter direction="bottom" size="md">
                  <Text size="lg">1. User Profile</Text>
                </Gutter>

                <SupplierSignupPageUserProfileForm
                  formSubmitting={formSubmitting}
                  timeZonesQueryData={timeZonesQueryData}
                />
              </Card>

              <Card>
                <Gutter direction="bottom" size="md">
                  <Text size="lg">2. Organization Details</Text>
                </Gutter>

                <SupplierSignupPageOrganizationDetailsForm
                  formSubmitting={formSubmitting}
                  timeZonesQueryData={timeZonesQueryData}
                />
              </Card>

              <Flex justify="end" fullWidth>
                <SubmitButton disabled={formSubmitting}>Submit</SubmitButton>
              </Flex>
            </Gap>
          </Form>

          <AuthTemplateHelp />
        </Gap>
      </Gutter>
    </div>
  );
}
